import React, { useMemo, useState } from 'react';
import BreadCatalogCard from './BreadCatalogCard';

export default function BreadCatalogList({
  items,
  categoryMeta,
  search,
  categoryFilter,
  onSelect,
  onToggleEnabled,
  onDelete,
  onEditCategoryPrice,
  loading,
}) {
  const [collapsed, setCollapsed] = useState({});

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch =
        !search ||
        item.name_es?.toLowerCase().includes(search.toLowerCase()) ||
        item.name_en?.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = !categoryFilter || item.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [items, search, categoryFilter]);

  const grouped = useMemo(() => {
    const groups = {};
    // Seed groups from categoryMeta so newly created (empty) categories
    // still render on the page before any breads are assigned to them.
    Object.keys(categoryMeta).forEach((k) => {
      groups[k] = [];
    });
    filtered.forEach((item) => {
      const cat = item.category || 'sin_categoria';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });

    // Sort items alphabetically within each category
    for (const cat of Object.keys(groups)) {
      groups[cat].sort((a, b) => (a.name_es || '').localeCompare(b.name_es || '', 'es'));
    }

    const sortedKeys = Object.keys(groups).sort((a, b) => {
      const orderA = categoryMeta[a]?.display_order ?? 999;
      const orderB = categoryMeta[b]?.display_order ?? 999;
      return orderA - orderB;
    });

    return sortedKeys.map((key) => {
      const groupItems = groups[key];
      const meta = categoryMeta[key];
      // Count breads in this category that have a per-bread minimum set
      const breadsWithMin = groupItems.filter((b) => b.min_qty != null).length;
      return {
        key,
        label: meta?.name_es || key,
        icon: meta?.icon || '🍞',
        items: groupItems,
        categoryPrice: meta?.price ?? 0,
        breadsWithMin,
      };
    });
  }, [filtered, categoryMeta]);

  const toggleCollapse = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="bc-skeleton-list">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bc-skeleton-card" />
        ))}
      </div>
    );
  }

  if (filtered.length === 0 && (search || categoryFilter)) {
    return (
      <div className="bc-empty">
        <span className="bc-empty-icon">🍞</span>
        <p>No se encontraron panes.</p>
      </div>
    );
  }

  return (
    <div className="bc-list">
      {grouped.map((group, groupIdx) => (
        <section className="bc-category-group" key={group.key}>
          {/* Category Header */}
          <div className="bc-category-header-wrapper">
            <div className="bc-category-header-left">
              <div className={`bc-category-icon-box ${groupIdx % 2 === 0 ? 'primary-tint' : 'secondary-tint'}`}>
                <span className="material-symbols-outlined">bakery_dining</span>
              </div>
              <div className="bc-category-name-block">
                <h3 className="bc-category-name">{group.label}</h3>
                <p className="bc-category-count">
                  {group.items.length} Producto{group.items.length !== 1 ? 's' : ''} registrado{group.items.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="bc-category-header-right">
              <div className="bc-category-price-block">
                <span className="bc-category-price-label">Precio</span>
                <span className="bc-category-price-value">
                  ${group.categoryPrice.toFixed(2)}
                </span>
              </div>
              {group.breadsWithMin > 0 && (
                <div className="bc-category-price-block">
                  <span className="bc-category-price-label">Con mín.</span>
                  <span className="bc-category-price-value">
                    {group.breadsWithMin}
                  </span>
                </div>
              )}
              <button
                className="bc-category-edit-btn"
                onClick={() => onEditCategoryPrice ? onEditCategoryPrice(group.key) : toggleCollapse(group.key)}
                type="button"
                title="Editar categoría"
              >
                <span className="material-symbols-outlined">edit</span>
              </button>
              <button
                className="bc-category-edit-btn"
                onClick={() => toggleCollapse(group.key)}
                type="button"
                title={collapsed[group.key] ? 'Expandir' : 'Colapsar'}
              >
                <span className="material-symbols-outlined">
                  {collapsed[group.key] ? 'expand_more' : 'expand_less'}
                </span>
              </button>
            </div>
          </div>

          {/* Product Table */}
          {!collapsed[group.key] && (
            <div className="bc-table-wrapper">
              <table className="bc-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Estado</th>
                    <th>Detalles Técnicos</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item, idx) => (
                    <BreadCatalogCard
                      key={item.id}
                      bread={item}
                      categoryMeta={categoryMeta[item.category]}
                      onClick={() => onSelect(item)}
                      onToggleEnabled={onToggleEnabled}
                      index={idx}
                      groupIdx={groupIdx}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {collapsed[group.key] && (
            <div className="bc-expand-hint">
              TOCA PARA EXPANDIR INVENTARIO
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
